import { Txt } from '@stenajs-webui/core';
import { FlatButton, PrimaryButton } from '@stenajs-webui/elements';
import { ModalActionButtons, ModalBody, ModalHeader } from '@stenajs-webui/modal';
import { type FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { UrgentMessageHTMLRenderer } from '../../../common-ui/UrgentMessageHTMLRenderer';
import { markUrgentMessageAsUnread } from '../../../services/notifications/notificationsActions';
import type { UrgentMessage } from '../../graphql/fragments/gql/UrgentMessage';
import { type TrackerCategory, trackEvent } from '../../tracking/trackerService';

interface UrgentMessageModalProps {
  onClose: () => void;
  trackerAction: string;
  trackerCategory: TrackerCategory;
  urgentMessage: UrgentMessage;
}

export const UrgentMessageModal: FC<UrgentMessageModalProps> = ({
  onClose,
  urgentMessage: { header, id, routes, textHtml },
  trackerAction,
  trackerCategory,
}) => {
  const dispatch = useDispatch();

  const markAsUnread = () => {
    trackEvent({
      category: trackerCategory,
      action: trackerAction,
      label: 'Remind me button',
    });

    dispatch(markUrgentMessageAsUnread(id));
    onClose();
  };

  const onCloseWithTracker = (label: string) => () => {
    trackEvent({
      category: trackerCategory,
      action: trackerAction,
      label,
    });

    onClose();
  };

  const onPressLink = useCallback(
    (url: string) => {
      trackEvent({
        category: trackerCategory,
        action: 'Pressed link in urgent message (modal)',
        label: url,
      });
    },
    [trackerCategory],
  );

  return (
    <ModalBody width={[null, null, 500]} maxWidth={'100%'}>
      <ModalHeader
        heading={header}
        onClickClose={onCloseWithTracker('Close message')}
        closeButtonAriaLabel={'Close'}
      />
      {routes.length > 0 && (
        <ul style={{ padding: 0, margin: 0, listStyleType: 'none' }}>
          {routes.map(r => (
            <li key={r.code}>
              <Txt variant={'overline'} size={'small'}>
                {r.longDescription}
              </Txt>
            </li>
          ))}
        </ul>
      )}
      <Txt style={{ overflowWrap: 'break-word' }}>
        <UrgentMessageHTMLRenderer html={textHtml} onPressLink={onPressLink} />
      </Txt>
      <ModalActionButtons>
        <FlatButton label={'Remind me'} onClick={markAsUnread} />
        <PrimaryButton
          label={'Ok, got it'}
          onClick={onCloseWithTracker("Don't remind me button")}
        />
      </ModalActionButtons>
    </ModalBody>
  );
};
